import { Buffer } from 'buffer';
import { Wallet } from './wallet';
import { ISO2_TO_NUMERIC } from './countryCodes';
import { 
  Contract, 
  rpc,
  Networks, 
  xdr,
  BASE_FEE,
  Address,
  nativeToScVal
} from '@stellar/stellar-sdk';
import { TransactionBuilder } from '@stellar/stellar-sdk';

// Base URL for backend calls (empty = same-origin relative path).
const CONTRACT_BACKEND_BASE = (
  (typeof process !== 'undefined' && (process as any).env?.REACT_APP_BACKEND_URL) || ''
) as string;

export class ContractClient {
  private wallet: Wallet;
  private network: Networks;
  private rpc: rpc.Server;
  // When set, the next `call()` submits its signed inner tx through the
  // backend fee-bump sponsor (service account pays the Soroban resource
  // fee) instead of the user's account. The user still signs once to
  // authorize; they just don't pay the fee. Reset per call.
  private sponsorNextSubmit = false;
  // V2 contract instances — one per domain
  private sessionContract: Contract;
  private vaultContract: Contract;
  private complianceContract: Contract;
  private coreContract: Contract;
  private vaultId: string;

  // Routes a V2 function name to the correct contract domain
  private static readonly V2_FUNCTION_ROUTING: Record<string, 'session' | 'vault' | 'compliance' | 'core'> = {
    // Session
    create_session: 'session', start_session: 'session', join_session: 'session', end_session: 'session',
    get_session: 'session', get_session_groups: 'session', get_live_session_id: 'session', set_live_session_id: 'session',
    create_trust_group: 'session', add_trust_group_member: 'session', remove_trust_group_member: 'session',
    get_trust_group: 'session', anchor_event: 'session', get_anchored_event: 'session',
    get_last_cell_id: 'session', set_zk_verifier: 'session',
    // Vault
    deposit: 'vault', withdraw: 'vault', transfer_between_countries: 'vault',
    execute_payment: 'vault', balance: 'vault', total_vault_balance: 'vault',
    register_passkey: 'vault', get_passkey: 'vault', set_webauthn_verifier: 'vault',
    // Compliance
    get_country_policy: 'compliance', get_user_compliance_state: 'compliance',
    get_kyc_data: 'compliance', set_country_policy: 'compliance', set_transfer_allowed: 'compliance',
    set_user_compliance_state: 'compliance', set_kyc_data: 'compliance',
    set_default_allow_all: 'compliance', check_deposit_allowed: 'compliance',
    check_withdrawal_allowed: 'compliance', check_transfer_allowed: 'compliance',
    record_transaction: 'compliance',
    // Core
    register_country: 'core', set_country_enabled: 'core', get_country_info: 'core',
    list_allowed_countries: 'core',
    set_country_admin: 'core', remove_country_admin: 'core', get_country_admin: 'core',
    set_registry: 'core', get_registry: 'core', get_admin: 'core',
  };

  // V1 function name → V2 function name renames
  private static readonly V1_TO_V2_RENAMES: Record<string, string> = {
    get_balance: 'balance',
    get_total_vault_balance: 'total_vault_balance',
    get_passkey_pubkey: 'get_passkey',
    add_to_trust_group: 'add_trust_group_member',
    remove_from_trust_group: 'remove_trust_group_member',
    submit_kyc_data: 'set_kyc_data',
    set_verifier: 'set_zk_verifier',
    // V2 migration: allowed/denied country list replaced by enabled flag on CountryInfoV2
    set_country_allowed: 'set_country_enabled',
    get_country_allowed: 'get_country_info',
    list_allowed_countries: 'get_country_info',
  };

  // ISO 3166-1 alpha-2 → numeric mapping for country code conversion
  static readonly ALPHA2_TO_NUMERIC: Record<string, number> = ISO2_TO_NUMERIC;

  // Reverse map: numeric → alpha-2 (2-letter codes preferred over 3-letter ISO3 codes)
  private static readonly NUMERIC_TO_ALPHA2: Record<number, string> = (() => {
    const map: Record<number, string> = {};
    for (const [code, num] of Object.entries(ISO2_TO_NUMERIC)) {
      // Prefer 2-letter codes; only overwrite if current entry is 3-letter
      if (!map[num] || (code.length === 2 && map[num].length !== 2)) {
        map[num] = code;
      }
    }
    return map;
  })();

  private getV2FunctionName(v1Name: string): string {
    return ContractClient.V1_TO_V2_RENAMES[v1Name] || v1Name;
  }

  private getContractForFn(v2FunctionName: string): Contract {
    const domain = ContractClient.V2_FUNCTION_ROUTING[v2FunctionName];
    switch (domain) {
      case 'session':    return this.sessionContract;
      case 'vault':      return this.vaultContract;
      case 'compliance': return this.complianceContract;
      case 'core':       return this.coreContract;
      default:           return this.coreContract; // fallback to core for unknown V2 functions
    }
  }

  alpha2ToNumeric(alpha2: string): number {
    if (typeof alpha2 === 'number') return alpha2;
    const s = String(alpha2).trim();
    if (/^\d+$/.test(s)) return parseInt(s, 10);
    return ContractClient.ALPHA2_TO_NUMERIC[s.toUpperCase()] || 0;
  }

  constructor(wallet: Wallet) {
    this.wallet = wallet;
    this.network = Networks.TESTNET;
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const rpcUrl = process.env.REACT_APP_SOROBAN_RPC_URL ||
      (isDevelopment
        ? `${window.location.protocol}//${window.location.hostname}:8080/api/soroban-rpc`
        : `${window.location.protocol}//${window.location.host}/api/soroban-rpc`);
    this.rpc = new rpc.Server(rpcUrl);

    // V2 contract IDs — required, no V1 fallback
    const sessionId    = process.env.REACT_APP_V2_SESSION_ID;
    const vaultId      = process.env.REACT_APP_V2_VAULT_ID;
    const complianceId = process.env.REACT_APP_V2_COMPLIANCE_ID;
    const coreId       = process.env.REACT_APP_V2_CORE_ID;

    if (!sessionId || !vaultId || !complianceId || !coreId) {
      throw new Error(
        'V2 contract IDs are required. Please set REACT_APP_V2_SESSION_ID, REACT_APP_V2_VAULT_ID, ' +
        'REACT_APP_V2_COMPLIANCE_ID, and REACT_APP_V2_CORE_ID in your environment variables.'
      );
    }

    this.vaultId          = vaultId;
    this.sessionContract  = new Contract(sessionId);
    this.vaultContract    = new Contract(vaultId);
    this.complianceContract = new Contract(complianceId);
    this.coreContract     = new Contract(coreId);
  }

  /**
   * The connected wallet, for flows that build their transaction elsewhere
   * (a V2 backend route returning unsigned XDR) and only need a signer.
   * Callers must not use this to bypass `call()` for contracts this client
   * already wraps.
   */
  getWallet(): Wallet {
    return this.wallet;
  }

  /** Network passphrase this client signs against. */
  getNetwork(): Networks {
    return this.network;
  }

  async init(admin: string, defaultAllowAll: boolean): Promise<void> {
    await this.call('init', admin, defaultAllowAll);
  }

  async getAdmin(): Promise<string | null> {
    try {
      const result = await this.call('get_admin');
      // Check if result is a special XDR error marker
      if (result === 'XDR_PARSING_ERROR') {
        throw new Error('XDR_PARSING_ERROR:Bad union switch: 1');
      }
      // get_admin returns Option<Address>
      // If result is void/undefined/null, contract is not initialized
      if (!result || result === null || result === undefined) {
        return null;
      }
      // Result might be an Address object or string - convert to string
      if (typeof result === 'string') {
        return result;
      }
      // If it's an object (Address), try to extract the string
      if (result && typeof result === 'object') {
        // Check if it has a toString method or address property
        if (result.toString && typeof result.toString === 'function') {
          const str = result.toString();
          // Stellar addresses can start with 'G' (account) or 'C' (contract)
          if (str && str.length === 56 && (str.startsWith('G') || str.startsWith('C'))) {
            return str;
          }
        }
        // Try to get address from object properties
        if ((result as any).address) {
          return String((result as any).address);
        }
        // console.warn('[ContractClient] getAdmin returned unexpected object:', result);
      }
      // Fallback: try to convert to string
      return String(result);
    } catch (error: any) {
      // Check if this is an XDR parsing error - re-throw it so caller can handle it
      if (error.message?.includes('XDR_PARSING_ERROR')) {
        // console.error('[ContractClient] XDR parsing error when getting admin:', error);
        throw error; // Re-throw XDR errors so caller can distinguish them
      }
      // console.error('[ContractClient] Failed to get admin:', error);
      // If get_admin fails (e.g., contract not initialized), return null
      return null;
    }
  }

  async setAdmin(newAdmin: string): Promise<void> {
    // console.log('[ContractClient] Setting new admin:', newAdmin);
    await this.call('set_admin', newAdmin);
  }

  async getSession(sessionId: number): Promise<any> {
    // console.log('[ContractClient] Getting session:', sessionId);
    try {
      const result = await this.call('get_session', sessionId);
      // console.log('[ContractClient] Session data:', JSON.stringify(result, null, 2));
      
      // The contract returns a Session struct, which is converted to a Map with symbol keys
      // The scValToJs method should now properly convert symbol keys to strings
      // Extract fields from the parsed result
      if (result && typeof result === 'object') {
        const session: any = {};
        
        // Session struct fields: player1, player2, p1_cell_id, p2_cell_id, 
        // p1_asset_tag, p2_asset_tag, state, created_ledger, p1_country, p2_country
        // Check if result has the fields directly (already parsed by scValToJs with symbol key support)
        if (result.player1 !== undefined && result.player1 !== null) {
          session.player1 = result.player1;
        }
        if (result.player2 !== undefined && result.player2 !== null) {
          session.player2 = result.player2;
        }
        if (result.state !== undefined) {
          // State is an enum variant - might be an array with the variant data
          // or already converted to a string
          // console.log('[ContractClient] Raw state value:', JSON.stringify(result.state, null, 2));
          
          if (Array.isArray(result.state) && result.state.length > 0) {
            // Enum variant wrapped in array - extract the variant value
            const variant = result.state[0];
            // console.log('[ContractClient] State variant:', JSON.stringify(variant, null, 2));
            
            // Try multiple extraction paths
            let stateStr: string | undefined;
            
            // Path 1: variant._value.data (Buffer data)
            if (variant?._value?.data && Array.isArray(variant._value.data)) {
              stateStr = String.fromCharCode(...variant._value.data);
              // console.log('[ContractClient] Extracted state from _value.data:', stateStr);
            }
            // Path 2: variant._value is a string
            else if (variant?._value && typeof variant._value === 'string') {
              stateStr = variant._value;
              // console.log('[ContractClient] Extracted state from _value (string):', stateStr);
            }
            // Path 3: variant is a string
            else if (typeof variant === 'string') {
              stateStr = variant;
              // console.log('[ContractClient] Extracted state from variant (string):', stateStr);
            }
            // Path 4: Check if _value has a different structure
            else if (variant?._value) {
              // Try to stringify and parse
              const valueStr = JSON.stringify(variant._value);
              if (valueStr.includes('data')) {
                // Try to extract from JSON
                try {
                  const parsed = JSON.parse(valueStr);
                  if (parsed.data && Array.isArray(parsed.data)) {
                    stateStr = String.fromCharCode(...parsed.data);
                    // console.log('[ContractClient] Extracted state from parsed _value.data:', stateStr);
                  }
                } catch (e) {
                  // Ignore parse errors
                }
              }
            }
            
            if (stateStr) {
              session.state = stateStr;
            } else {
              // console.warn('[ContractClient] Could not extract state, using raw value');
              session.state = result.state as any;
            }
          } else if (typeof result.state === 'string') {
            session.state = result.state;
          } else {
            // Try to extract state from object
            session.state = result.state as any;
          }
        }
        if (result.p1_cell_id !== undefined) {
          session.p1CellId = result.p1_cell_id;
          session.p1_cell_id = result.p1_cell_id;
        }
        if (result.p2_cell_id !== undefined) {
          session.p2CellId = result.p2_cell_id;
          session.p2_cell_id = result.p2_cell_id;
        }
        if (result.p1_country !== undefined) {
          session.p1Country = result.p1_country;
          session.p1_country = result.p1_country;
        }
        if (result.p2_country !== undefined) {
          session.p2Country = result.p2_country;
          session.p2_country = result.p2_country;
        }
        if (result.created_ledger !== undefined) {
          session.createdLedger = result.created_ledger;
          session.created_ledger = result.created_ledger;
        }
        
        // Parse new fields for public sessions
        if (result.players !== undefined) {
          session.players = result.players;
        }
        if (result.is_public !== undefined) {
          session.is_public = result.is_public;
          session.isPublic = result.is_public; // Also set camelCase version
        }
        
        // If we got some fields, return the parsed session
        if (Object.keys(session).length > 0) {
          // console.log('[ContractClient] Parsed session:', session);
          // console.log('[ContractClient] Session state:', session.state);
          // console.log('[ContractClient] Player1:', session.player1);
          // console.log('[ContractClient] Player2:', session.player2);
          // console.log('[ContractClient] Players array:', session.players);
          // console.log('[ContractClient] Is public:', session.is_public || session.isPublic);
          return session;
        }
        
        // Fallback: return result as-is if parsing failed (but include all fields)
        // Make sure to include players and is_public even in fallback
        if (result.is_public !== undefined) {
          result.isPublic = result.is_public;
        }
        return result;
      }
      
      return result;
    } catch (error) {
      // console.error('[ContractClient] Failed to get session:', error);
      return null;
    }
  }

  async getCountryPolicy(country?: number): Promise<[boolean, number, number]> {
    // V2: get_country_policy takes a country: u32 and returns Option<CountryPolicyV2>
    // V1 returned a tuple [defaultAllowAll, dailyLimit, monthlyLimit]
    // For V2 callers that still expect the tuple, return a sensible default if no country provided
    const countryArg = country ?? 840; // default US
    const result = await this.call('get_country_policy', countryArg);
    return result as [boolean, number, number];
  }

  async getCountryAllowed(country: number): Promise<boolean | null> {
    try {
      // V2 has no get_country_allowed; check enabled flag in CountryInfoV2 instead.
      const result = await this.call('get_country_info', country);
      if (!result || typeof result !== 'object') return false;
      return (result as any).enabled === true;
    } catch {
      return false;
    }
  }

  async listAllowedCountries(page: number, pageSize: number): Promise<number[]> {
    const result = await this.call('list_allowed_countries', page, pageSize);
    return result as number[];
  }

  async setCountryAllowed(country: number, allowed: boolean): Promise<void> {
    if (allowed) {
      // Register the country first (idempotent).
      // register_country sets enabled=true by default, so if we just created the record
      // we can skip the redundant set_country_enabled call — this also avoids a race
      // where set_country_enabled is simulated before register_country is confirmed.
      const alreadyExisted = await this.ensureCountryRegistered(country);
      if (!alreadyExisted) {
        // Freshly registered → already enabled. Nothing more to do.
        return;
      }
      // Country already existed (was previously disabled) → fall through to re-enable it.
    }
    await this.call('set_country_enabled', country, allowed);
  }

  async setDefaultAllowAll(value: boolean): Promise<void> {
    await this.call('set_default_allow_all', value);
  }

  // Country admin management
  async setCountryAdmin(country: number, countryAdmin: string): Promise<void> {
    await this.call('set_country_admin', country, countryAdmin);
  }

  async removeCountryAdmin(country: number): Promise<void> {
    await this.call('remove_country_admin', country);
  }

  // Global fee configuration (main admin only)
  async setGlobalFeeConfig(feePercentage: number, countryAdminShare: number): Promise<void> {
    // feePercentage and countryAdminShare are in basis points (e.g., 100 = 1%, 3000 = 30%)
    await this.call('set_global_fee_config', feePercentage, countryAdminShare);
  }

  async getGlobalFeeConfig(): Promise<{ fee_percentage: number; country_admin_share: number } | null> {
    try {
      const result = await this.call('get_global_fee_config');
      if (result && typeof result === 'object') {
        return {
          fee_percentage: typeof result.fee_percentage === 'number' ? result.fee_percentage : Number(result.fee_percentage || 0),
          country_admin_share: typeof result.country_admin_share === 'number' ? result.country_admin_share : Number(result.country_admin_share || 0)
        };
      }
      return null;
    } catch (error) {
      // console.error('[ContractClient] Failed to get fee config:', error);
      return null;
    }
  }

  async getCountryAdmin(country: number): Promise<string | null> {
    try {
      const result = await this.call('get_country_admin', country);
      if (!result || result === null || result === undefined) {
        return null;
      }
      if (typeof result === 'string') {
        return result;
      }
      return String(result);
    } catch (error) {
      // console.error('[ContractClient] Failed to get country admin:', error);
      return null;
    }
  }

  async setCountryDenied(country: number, denied: boolean): Promise<void> {
    // V2: use set_country_enabled with inverted flag
    await this.call('set_country_enabled', country, !denied);
  }

  // ========== Country Vault Functions ==========

  /**
   * Register a country in the country vault system (admin-only)
   * V2: register_country(numeric_code: u32, alpha2: BytesN<2>, name: String)
   */
  async registerCountry(countryCode: string, countryName: string, _adminAddress: string): Promise<boolean> {
    const numericCode = this.alpha2ToNumeric(countryCode);
    // V2 expects alpha2 as BytesN<2> — encode the two ASCII chars as a Uint8Array
    const tag = countryCode.toUpperCase().substring(0, 2).padEnd(2, '\0');
    const alpha2Bytes = new Uint8Array([tag.charCodeAt(0), tag.charCodeAt(1)]);
    return await this.call('register_country', numericCode, alpha2Bytes, countryName) as boolean;
  }

  /**
   * Enable or disable a country (admin-only)
   * V2: set_country_enabled(numeric_code: u32, enabled: bool)
   * Automatically registers the country first when enabling (idempotent — ignores #4 AlreadyRegistered).
   */
  async setCountryEnabled(countryCode: string | number, enabled: boolean, _adminAddress?: string): Promise<boolean> {
    const numericCode = typeof countryCode === 'number' ? countryCode : this.alpha2ToNumeric(countryCode);
    if (enabled) {
      await this.ensureCountryRegistered(numericCode);
    }
    return await this.call('set_country_enabled', numericCode, enabled) as boolean;
  }

  /**
   * Register a country if it isn't already registered.
   * Returns true  if the country was ALREADY registered (AlreadyRegistered #4).
   * Returns false if it was freshly registered (now enabled by default).
   * Throws for any other error.
   */
  private async ensureCountryRegistered(numericCode: number): Promise<boolean> {
    const alpha2 = ContractClient.NUMERIC_TO_ALPHA2[numericCode] || 'XX';
    const name   = alpha2;
    const alpha2Bytes = new Uint8Array([
      alpha2.charCodeAt(0) || 88,
      alpha2.charCodeAt(1) || 88,
    ]);
    try {
      await this.call('register_country', numericCode, alpha2Bytes, name);
      return false; // freshly registered, enabled=true by contract default
    } catch (e: any) {
      // #4 = CountryAlreadyRegistered — safe to ignore, country already exists
      const msg = e?.message || '';
      if (!msg.includes('#4') && !msg.includes('AlreadyRegistered') && !msg.includes('already')) {
        throw e;
      }
      return true; // was already registered
    }
  }

  /**
   * Get country information
   */
  async getCountryInfo(countryCode: string | number): Promise<any> {
    try {
      // Contract expects numeric_code: u32, not an alpha-2 string
      const numericCode = typeof countryCode === 'number'
        ? countryCode
        : this.alpha2ToNumeric(String(countryCode));
      const result = await this.call('get_country_info', numericCode);
      return result;
    } catch (error) {
      // console.error('[ContractClient] Failed to get country info:', error);
      return null;
    }
  }

  /**
   * Deposit tokens to country-specific vault
   * Note: This requires WebAuthn authentication - the actual implementation should be done via backend API
   */
  async deposit(
    userAddress: string,
    countryCode: string,
    asset: string,
    amount: number,
    signaturePayload: string,
    webauthnSignature: string,
    webauthnAuthenticatorData: string,
    webauthnClientData: string
  ): Promise<boolean> {
    // console.log('[ContractClient] deposit:', countryCode, asset, amount);
    
    // V2 deposit: country is u32, WebAuthn params wrapped in WebAuthnSigData struct.
    // console.log('[ContractClient] deposit - calling with args:', {
    //   arg0_userAddress: userAddress,
    //   arg1_countryCodeU32: countryCode,
    //   arg1_countryCodeScVal: {
    //     type: 'ScVal',
    //     switchType: scValType.name,
    //     bytesLength: scValBytesValue.length,
    //     bytes: Array.from(scValBytesValue),
    //     willPassThrough: true
    //   },
    //   arg2_asset: asset,
    //   arg3_amount: amount,
    //   arg4_signaturePayloadLength: signaturePayloadBytes.length,
    //   arg5_webauthnSignatureLength: webauthnSignatureBytes.length,
    //   arg6_webauthnAuthenticatorDataLength: webauthnAuthenticatorDataBytes.length,
    //   arg7_webauthnClientDataLength: webauthnClientDataBytes.length
    // });
    
    // V2 deposit: country is u32 (not BytesN<2>), and WebAuthn params are wrapped in WebAuthnSigData struct.
    // Signature: deposit(user, country: u32, asset, amount, webauthn_sig: Option<WebAuthnSigData>, sig_payload: Option<Bytes>)
    const numericCountry = this.alpha2ToNumeric(countryCode);
    const countryU32ScVal = xdr.ScVal.scvU32(numericCountry);

    // Build WebAuthnSigData struct ScVal (sorted symbol keys as required by Soroban)
    let webauthnSigDataScVal: xdr.ScVal = xdr.ScVal.scvVoid();
    if (webauthnSignature && webauthnAuthenticatorData && webauthnClientData) {
      const waSigBytes  = Buffer.from(webauthnSignature, 'base64');
      const waAuthBytes = Buffer.from(webauthnAuthenticatorData, 'base64');
      const waClientBytes = Buffer.from(webauthnClientData, 'base64');
      webauthnSigDataScVal = xdr.ScVal.scvMap([
        new xdr.ScMapEntry({ key: xdr.ScVal.scvSymbol('authenticator_data'), val: xdr.ScVal.scvBytes(waAuthBytes) }),
        new xdr.ScMapEntry({ key: xdr.ScVal.scvSymbol('client_data_json'),   val: xdr.ScVal.scvBytes(waClientBytes) }),
        new xdr.ScMapEntry({ key: xdr.ScVal.scvSymbol('signature'),          val: xdr.ScVal.scvBytes(waSigBytes) }),
      ]);
    }

    const sigPayloadScVal: xdr.ScVal = signaturePayload
      ? xdr.ScVal.scvBytes(Buffer.from(signaturePayload, 'utf8'))
      : xdr.ScVal.scvVoid();

    return await this.call(
      'deposit',
      userAddress,
      countryU32ScVal,
      asset,
      amount,
      webauthnSigDataScVal,
      sigPayloadScVal
    ) as boolean;
  }

  /**
   * Execute payment from country-specific vault
   * Note: This requires WebAuthn authentication - the actual implementation should be done via backend API
   */
  async executePayment(
    signerAddress: string,
    countryCode: string,
    destination: string,
    amount: number,
    asset: string,
    signaturePayload: string,
    webauthnSignature: string,
    webauthnAuthenticatorData: string,
    webauthnClientData: string
  ): Promise<boolean> {
    // console.log('[ContractClient] executePayment:', countryCode, destination, amount);
    // Convert signature payload and WebAuthn data to Bytes
    const signaturePayloadBytes = Buffer.from(signaturePayload, 'utf8');
    const webauthnSignatureBytes = Buffer.from(webauthnSignature, 'base64');
    const webauthnAuthenticatorDataBytes = Buffer.from(webauthnAuthenticatorData, 'base64');
    const webauthnClientDataBytes = Buffer.from(webauthnClientData, 'base64');
    
    // Pass individual WebAuthn parameters (not as struct) - following the guide
    return await this.call(
      'execute_payment',
      signerAddress,
      countryCode,
      destination,
      amount,
      asset,
      signaturePayloadBytes,
      webauthnSignatureBytes,
      webauthnAuthenticatorDataBytes,
      webauthnClientDataBytes
    ) as boolean;
  }

  /**
   * Get balance for a specific country and asset
   */
  async getBalance(userAddress: string, countryCode: string, asset: string): Promise<number> {
    try {
      // V2: renamed to 'balance', country is u32 numeric (routing map handles the rename)
      const numericCountry = this.alpha2ToNumeric(countryCode);
      const result = await this.call('get_balance', userAddress, numericCountry, asset);
      // Result is i128, convert to number
      if (typeof result === 'number') {
        return result;
      }
      if (typeof result === 'bigint') {
        return Number(result);
      }
      if (result && typeof result === 'object' && 'lo' in result) {
        // i128 is returned as { hi, lo }
        return Number(result.lo || 0);
      }
      return 0;
    } catch (error) {
      // console.error('[ContractClient] Failed to get balance:', error);
      return 0;
    }
  }

  /**
   * Get all asset balances for a user in a specific country
   */
  async getUserCountryBalances(userAddress: string, countryCode: string): Promise<Map<string, number>> {
    try {
      const result = await this.call('get_user_country_balances', userAddress, countryCode);
      // Result is Map<Address, i128>
      if (result && typeof result === 'object') {
        const balances = new Map<string, number>();
        if (result instanceof Map) {
          result.forEach((value: any, key: any) => {
            const assetAddress = typeof key === 'string' ? key : String(key);
            const balance = typeof value === 'number' ? value : (value?.lo ? Number(value.lo) : 0);
            balances.set(assetAddress, balance);
          });
        }
        return balances;
      }
      return new Map();
    } catch (error) {
      // console.error('[ContractClient] Failed to get user country balances:', error);
      return new Map();
    }
  }

  /**
   * Get all countries where user has balances
   */
  async getUserCountries(userAddress: string): Promise<string[]> {
    try {
      const result = await this.call('get_user_countries', userAddress);
      // Result is Vec<String>
      if (Array.isArray(result)) {
        return result.map((code: any) => String(code));
      }
      return [];
    } catch (error) {
      // console.error('[ContractClient] Failed to get user countries:', error);
      return [];
    }
  }

  /**
   * Get total balance across all countries for a specific asset
   */
  async getTotalBalance(userAddress: string, asset: string): Promise<number> {
    try {
      const result = await this.call('get_total_balance', userAddress, asset);
      // Result is i128
      if (typeof result === 'number') {
        return result;
      }
      if (typeof result === 'bigint') {
        return Number(result);
      }
      if (result && typeof result === 'object' && 'lo' in result) {
        return Number(result.lo || 0);
      }
      return 0;
    } catch (error) {
      // console.error('[ContractClient] Failed to get total balance:', error);
      return 0;
    }
  }

  async setVerifier(verifierId: string, useBackend: boolean = true): Promise<void> {
    // const _verifierPreview = verifierId.substring(0, 8) + '...' + verifierId.substring(verifierId.length - 4);
    // console.log('[ContractClient] setVerifier called with:', _verifierPreview);
    
    // Use backend API to avoid user prompts and fees
    if (useBackend) {
      try {
        const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const backendUrl = isDevelopment 
          ? `${window.location.protocol}//${window.location.hostname}:8080/api/contract/admin`
          : '/api/contract/admin';
        
        const response = await fetch(backendUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            // V2: set_zk_verifier lives on the session contract
            contractId: process.env.REACT_APP_V2_SESSION_ID,
            functionName: 'set_zk_verifier',
            parameters: [verifierId],
          }),
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || error.error || 'Failed to set verifier');
        }
        
        await response.json(); // Response consumed but not used (commented out console log)
        // console.log('[ContractClient] Γ£à set_verifier transaction completed via backend:', _result.transactionHash);
        return;
      } catch (error: any) {
        // console.error('[ContractClient] Backend setVerifier failed, falling back to direct call:', error);
        // Fall through to direct call if backend fails
      }
    }
    
    // Fallback: direct call (requires user approval)
    // Convert string to Address - Contract.call() will handle the conversion
    // This calls set_verifier on the MAIN contract (not the verifier contract)
    // The main contract stores the verifier address for later use
    await this.call('set_verifier', verifierId);
    // console.log('[ContractClient] Γ£à set_verifier transaction completed');
  }

  async setWebauthnVerifier(verifierId: string, useBackend: boolean = true): Promise<void> {
    // const _verifierPreview = verifierId.substring(0, 8) + '...' + verifierId.substring(verifierId.length - 4);
    // console.log('[ContractClient] setWebauthnVerifier called with:', _verifierPreview);
    
    // Use backend API to avoid user prompts and fees
    if (useBackend) {
      try {
        const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const backendUrl = isDevelopment 
          ? `${window.location.protocol}//${window.location.hostname}:8080/api/contract/admin`
          : '/api/contract/admin';
        
        const response = await fetch(backendUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            // V2: set_webauthn_verifier lives on the vault contract
            contractId: process.env.REACT_APP_V2_VAULT_ID,
            functionName: 'set_webauthn_verifier',
            parameters: [verifierId],
          }),
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || error.error || 'Failed to set WebAuthn verifier');
        }
        
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _result = await response.json();
        // console.log('[ContractClient] Γ£à set_webauthn_verifier transaction completed via backend:', _result.transactionHash);
        return;
      } catch (error: any) {
        // console.error('[ContractClient] Backend setWebauthnVerifier failed, falling back to direct call:', error);
        // Fall through to direct call if backend fails
      }
    }
    
    // Fallback: direct call (requires user approval)
    // This calls set_webauthn_verifier on the MAIN contract
    // The main contract stores the WebAuthn verifier address for later use
    await this.call('set_webauthn_verifier', verifierId);
    // console.log('[ContractClient] Γ£à set_webauthn_verifier transaction completed');
  }

  async getWebauthnVerifier(): Promise<string | null> {
    try {
      const result = await this.call('get_webauthn_verifier');
      if (!result || result === null || result === undefined) {
        return null;
      }
      // Convert Address to string
      return String(result);
    } catch (error: any) {
      // console.error('[ContractClient] Failed to get WebAuthn verifier:', error);
      return null;
    }
  }

  async createSession(isPublic: boolean = false): Promise<number> {
    // console.log('[ContractClient] Creating session (isPublic:', isPublic, ')...');
    // V2: create_session(is_public, country: Option<u32>, trust_group_id: Option<u32>)
    // Sponsor the fee so the user isn't charged the Soroban resource fee.
    this.sponsorNextSubmit = true;
    const result = await this.call('create_session', isPublic, null, null);
    // console.log('[ContractClient] Session created with ID:', result);
    return result as number;
  }

  /**
   * "Go live": create an Active session with the caller already present,
   * scoped either fully public or to one or more trust groups. Replaces
   * the retired external Game Hub start_game.
   *
   * start_session(creator, is_public, country: Option<u32>, trust_group_ids: Vec<u32>)
   * Passing a non-empty group list forces the session private on-chain.
   */
  async startSession(
    creator: string,
    isPublic: boolean,
    country: number | null = null,
    trustGroupIds: number[] = [],
  ): Promise<number> {
    const groupsScVal = xdr.ScVal.scvVec((trustGroupIds || []).map((id) => xdr.ScVal.scvU32(id)));
    // Sponsor the fee so "going live" doesn't charge the user.
    this.sponsorNextSubmit = true;
    const result = await this.call('start_session', creator, isPublic, country, groupsScVal);
    return result as number;
  }

  /** Read a session's trust-group scope (empty for public / legacy sessions). */
  async getSessionGroups(sessionId: number): Promise<number[]> {
    try {
      const result = await this.call('get_session_groups', sessionId);
      if (Array.isArray(result)) return result.map((n: any) => Number(n)).filter((n) => Number.isInteger(n));
      return [];
    } catch {
      return [];
    }
  }

  async getLiveSessionId(): Promise<number | null> {
    try {
      const result = await this.call('get_live_session_id');
      return result as number | null;
    } catch (error) {
      // console.error('[ContractClient] Failed to get live session ID:', error);
      return null;
    }
  }

  async setLiveSessionId(sessionId: number): Promise<void> {
    // console.log('[ContractClient] Setting live session ID:', sessionId);
    await this.call('set_live_session_id', sessionId);
  }

  async updateLocation(
    sessionId: number,
    cellId: number,
    assetTag: Uint8Array,
    country: number,
    locationProof?: { proof: Uint8Array; publicInputs: number[] }
  ): Promise<void> {
    // console.log('[ContractClient] Updating location in session:', sessionId);
    
    const callerStr = await this.wallet.getPublicKey();
    
    // Use backend endpoint to sign with SERVICE_ACCOUNT_SECRET_KEY
    // This avoids wallet prompts for frequent location updates
    // Always try backend first (use relative path in production, localhost in development)
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    try {
      // Use the same pattern as joinSession for location proof
      let proofData: any = null;
      if (locationProof) {
        // Ensure proof is exactly 64 bytes (BytesN<64>)
        let proofBytes = locationProof.proof;
        if (proofBytes.length !== 64) {
          const padded = new Uint8Array(64);
          padded.set(proofBytes.slice(0, 64), 0);
          proofBytes = padded;
        }
        
        proofData = {
          proof: Array.from(proofBytes),
          public_inputs: locationProof.publicInputs,
        };
      }
      
      // Use relative path if backendUrl is empty (production)
      const updateLocationUrl = backendUrl 
        ? `${backendUrl}/api/contract/update-location`
        : '/api/contract/update-location';
      
      // console.log('[ContractClient] Attempting backend update-location:', {
      //   url: updateLocationUrl,
      //   sessionId,
      //   caller: callerStr.substring(0, 8) + '...',
      // });
      
      const response = await fetch(updateLocationUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contractId: process.env.REACT_APP_V2_SESSION_ID,
          caller: callerStr,
          session_id: sessionId,
          cell_id: cellId,
          asset_tag: Array.from(assetTag),
          country,
          location_proof: proofData,
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Backend update-location failed: ${response.status} ${errorText}`);
      }
      
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _result = await response.json();
      // console.log('[ContractClient] Γ£à Location updated via backend:', _result);
      return;
    } catch (error: any) {
      // console.error('[ContractClient] Backend update-location failed, falling back to direct call:', error);
      // Fall through to direct call if backend fails
    }
    
    // Fallback: direct call (will require wallet approval)
    // Use the same pattern as joinSession for location proof
    let proofDataScVal: xdr.ScVal | null = null;
    if (locationProof) {
      // Ensure proof is exactly 64 bytes (BytesN<64>)
      let proofBytes = locationProof.proof;
      if (proofBytes.length !== 64) {
        const padded = new Uint8Array(64);
        padded.set(proofBytes.slice(0, 64), 0);
        proofBytes = padded;
      }
      
      const proofScVal = nativeToScVal(proofBytes);
      const publicInputsScVal = nativeToScVal(locationProof.publicInputs);
      
      const mapEntries: xdr.ScMapEntry[] = [];
      mapEntries.push(
        new xdr.ScMapEntry({
          key: xdr.ScVal.scvSymbol('proof'),
          val: proofScVal,
        })
      );
      mapEntries.push(
        new xdr.ScMapEntry({
          key: xdr.ScVal.scvSymbol('public_inputs'),
          val: publicInputsScVal,
        })
      );
      
      proofDataScVal = xdr.ScVal.scvMap(mapEntries);
    }
    
    // Call with native values - the call method will handle conversion
    // Note: Since we removed require_auth from the contract, this will use simulation
    // and won't require wallet approval if update_location is in isReadOperation
    await this.call(
      'update_location',
      callerStr,
      sessionId,
      cellId,
      assetTag,
      country,
      proofDataScVal || null
    );
  }

  /**
   * Anchor an event on-chain (idempotent)
   * Called only when GeoLink determines an event is "anchor-worthy"
   * This replaces frequent update_location calls with event-boundary anchoring
   */
  async anchorEvent(
    account: string,
    eventId: Uint8Array | Buffer,      // Event ID as bytes (will be hashed in contract)
    eventType: string,                  // "CELL_TRANSITION" | "RULE_TRIGGERED" | "CHECKPOINT"
    cellId: bigint | number,            // Current cell ID
    occurredAt: bigint | number,        // Unix timestamp in seconds
    prevCellId?: bigint | number | null, // Previous cell ID (for transitions)
    ruleId?: string | null,             // Rule ID if event_type is RULE_TRIGGERED
    commitment?: Uint8Array | Buffer,   // Hash of off-chain evidence (32 bytes, can be zero)
  ): Promise<{ txHash?: string; hash?: string; ledger?: number }> {
    // console.log('[ContractClient] anchorEvent:', { eventType, cellId, occurredAt });

    const callerStr = account;
    
    // Use backend endpoint to sign with SERVICE_ACCOUNT_SECRET_KEY
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    try {
      // Convert commitment to array if provided, otherwise use zero hash
      let commitmentArray: number[] = [];
      if (commitment && commitment.length === 32) {
        commitmentArray = Array.from(commitment);
      } else {
        // Default to zero hash (32 bytes of zeros)
        commitmentArray = new Array(32).fill(0);
      }

      // Convert eventId to array
      const eventIdArray = Array.from(eventId);

      const anchorEventUrl = backendUrl 
        ? `${backendUrl}/api/contract/anchor-event`
        : '/api/contract/anchor-event';
      
      // console.log('[ContractClient] Attempting backend anchor-event:', {
      //   url: anchorEventUrl,
      //   eventType,
      //   caller: callerStr.substring(0, 8) + '...',
      // });
      
      const response = await fetch(anchorEventUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contractId: process.env.REACT_APP_V2_SESSION_ID,
          caller: callerStr,
          event_id: eventIdArray,
          event_type: eventType,
          cell_id: cellId.toString(),
          occurred_at: occurredAt.toString(),
          prev_cell_id: prevCellId ? prevCellId.toString() : null,
          rule_id: ruleId || null,
          commitment: commitmentArray,
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Backend anchor-event failed: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
      // console.log('[ContractClient] Γ£à Event anchored via backend:', result);
      return {
        txHash: result.txHash || result.hash,
        hash: result.txHash || result.hash,
        ledger: result.ledger,
      };
    } catch (error: any) {
      // console.error('[ContractClient] Backend anchor-event failed:', error);
      throw error;
    }
  }

  async joinSession(
    sessionId: number,
    cellId: number,
    assetTag: Uint8Array,
    country: number,
    locationProof?: { proof: Uint8Array; publicInputs: number[] }
  ): Promise<void> {
    // First, verify the session exists and validate preconditions
    // Use read-only getSession which doesn't affect sequence number
    try {
      const session = await this.getSession(sessionId);
      if (!session) {
        throw new Error(`Session ${sessionId} not found. Please create a session first.`);
      }
      
      // Validate session state
      const sessionState = session.state;
      // Normalize state string (handle case variations)
      const normalizedState = typeof sessionState === 'string' ? sessionState.toLowerCase() : String(sessionState).toLowerCase();
      
      // Check if player is already in session
      const callerStr = await this.wallet.getPublicKey();
      
      // For public sessions, check the players array; for private sessions, check player1/player2
      const isPublic = session.is_public || session.isPublic;
      if (isPublic) {
        // Public session: can join even if Active, check players array
        if (normalizedState === 'ended') {
          throw new Error(`Session ${sessionId} has ended. Cannot join.`);
        }
        
        // Check players array for public sessions
        if (session.players && Array.isArray(session.players)) {
          const playerExists = session.players.some((p: any) => {
            const playerAddr = p.address || p.player_address || (typeof p === 'string' ? p : null);
            if (!playerAddr) return false;
            return playerAddr === callerStr || String(playerAddr) === callerStr;
          });
          if (playerExists) {
            throw new Error(`You are already in session ${sessionId}`);
          }
        }
        // Also check legacy fields as fallback
        if (session.player1 === callerStr || session.player2 === callerStr) {
          throw new Error(`You are already in session ${sessionId}`);
        }
      } else {
        // Private session: must be Waiting, check player1/player2 (legacy behavior)
        if (normalizedState !== 'waiting') {
          throw new Error(`Session ${sessionId} is not in Waiting state. Current state: ${sessionState}`);
        }
        
        if (session.player1 === callerStr || session.player2 === callerStr) {
          throw new Error(`You are already in session ${sessionId}`);
        }
        
        // Check if private session is full (only 2 players allowed)
        if (session.player1 && session.player2) {
          throw new Error(`Session ${sessionId} is full`);
        }
      }
      
      // Check if country is allowed (read-only check)
      try {
        // V2: get_country_policy takes country: u32 and returns Option<CountryPolicyV2>
        // Fall through gracefully — the contract itself will enforce policy
        const policy = await this.call('get_country_policy', country);
        if (policy && typeof policy === 'object' && policy.restricted === true) {
          throw new Error(`Country ${country} is restricted. Please check the country policy.`);
        }
      } catch (error: any) {
        if (error.message?.includes('restricted')) throw error;
        // If policy check fails, let the contract handle it
        // console.warn('[ContractClient] Could not verify country policy, contract will validate:', error);
      }
      
      // console.log('[ContractClient] Session validated, proceeding to join:', {
      //   sessionId,
      //   state: sessionState,
      //   player1: session.player1 || 'None',
      //   player2: session.player2 || 'None',
      //   caller: callerStr,
      // });
    } catch (error) {
      // console.error('[ContractClient] Error validating session before join:', error);
      throw error instanceof Error ? error : new Error(`Cannot join session ${sessionId}: ${String(error)}`);
    }
    
    // Add a small delay to ensure account sequence is fresh after createSession
    // This helps prevent txBadSeq errors
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Get caller address and convert to ScVal Address
    const callerStr = await this.wallet.getPublicKey();
    
    // console.log('[ContractClient] Joining session:', {
    //   sessionId,
    //   cellId,
    //   country,
    //   caller: callerStr,
    //   hasProof: !!locationProof,
    //   assetTagLength: assetTag.length,
    //   assetTagHex: Array.from(assetTag.slice(0, 8)).map(b => b.toString(16).padStart(2, '0')).join('') + '...',
    //   assetTagIsZero: Array.from(assetTag).every(b => b === 0) ? 'WARNING: All zeros!' : 'OK'
    // });
    
    // Warn if asset tag is all zeros (placeholder)
    if (Array.from(assetTag).every(b => b === 0)) {
      // console.warn('[ContractClient] WARNING: Asset tag is all zeros (placeholder). This may cause issues.');
    }
    const callerAddress = new Address(callerStr);
    const callerScVal = xdr.ScVal.scvAddress(callerAddress.toScAddress());
    
    // V2 join_session always passes null for proof_envelope (ProofEnvelope not yet wired).
    
    // V2: join_session(caller, session_id, cell_id: u32, country: u32, asset_tag: Option<BytesN<32>>, proof_envelope: Option<ProofEnvelope>)
    // Country and asset_tag order is SWAPPED vs V1; proof_envelope has a new struct shape
    // Sponsor the fee so joining a live-presence session doesn't charge the user.
    this.sponsorNextSubmit = true;
    await this.call('join_session', callerScVal, sessionId, cellId, country, assetTag, null);
    // console.log('[ContractClient] Successfully joined session:', sessionId);
  }

  async adminDeleteSession(sessionId: number): Promise<void> {
    await this.call('admin_delete_session', sessionId);
  }

  async adminDeleteAllSessions(): Promise<number> {
    // console.log('[ContractClient] Deleting all sessions...');
    const result = await this.call('admin_delete_all_sessions');
    // console.log('[ContractClient] Deleted sessions count:', result);
    return result as number;
  }

  private async call(functionName: string, ...args: any[]): Promise<any> {
    // console.log(`[ContractClient.call] Calling ${functionName} with ${args.length} args`);
    // Consume the one-shot sponsor flag immediately so it never leaks to a
    // later call() even if this one throws before reaching submission.
    const useSponsor = this.sponsorNextSubmit;
    this.sponsorNextSubmit = false;
    const publicKey = await this.wallet.getPublicKey();
    
    // Get fresh account data right before building transaction to avoid sequence issues
    // This ensures we always have the latest sequence number
    // Retry a few times if account fetch fails (might be temporary network issue)
    let sourceAccount;
    let retries = 3;
    while (retries > 0) {
      try {
        sourceAccount = await this.rpc.getAccount(publicKey);
        break;
      } catch (error: any) {
        retries--;
        if (retries === 0) {
          throw new Error(`Failed to get account after 3 retries: ${error.message || error}`);
        }
        // console.warn(`[ContractClient.call] Failed to get account, retrying... (${retries} left)`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    // TypeScript guard: sourceAccount should always be set here, but add check for safety
    if (!sourceAccount) {
      throw new Error('Failed to get account: sourceAccount is undefined');
    }
    
    // console.log(`[ContractClient.call] Account sequence for ${functionName}:`, sourceAccount.sequenceNumber());
    
    // Convert all arguments to ScVal
    const scValArgs = args.map(arg => {
      // If already ScVal, return as is
      if (arg instanceof xdr.ScVal) {
        return arg;
      }
      // Convert based on type
      if (typeof arg === 'number') {
        // Check function name and argument position to determine if we need i128 or u32
        // Functions that use i128: 
        //   - set_global_fee_config: both args are i128
        //   - transfer_between_countries: amount is i128
        //   - deposit: amount is i128
        // Functions that use u32: 
        //   - set_country_allowed: country is u32
        //   - set_country_admin: country is u32
        //   - create_session: returns u32
        // For set_global_fee_config, both parameters are i128
        if (functionName === 'set_global_fee_config') {
          // Convert to i128 using Int128Parts (same as deposit function)
          const amountBigInt = BigInt(arg);
          const hi = amountBigInt >> BigInt(64);
          const lo = amountBigInt & BigInt('0xFFFFFFFFFFFFFFFF');
          const amountI128 = new xdr.Int128Parts({
            hi: xdr.Int64.fromString(hi.toString()),
            lo: xdr.Uint64.fromString(lo.toString())
          });
          return xdr.ScVal.scvI128(amountI128);
        }
        // Default to u32 for IDs and counts
        return xdr.ScVal.scvU32(arg);
      }
      if (typeof arg === 'boolean') {
        return xdr.ScVal.scvBool(arg);
      }
      if (typeof arg === 'string') {
        // Check if it's an address (Stellar address format: starts with G or C and is 56 chars)
        // G = account address, C = contract ID
        if (arg.length === 56 && (arg.startsWith('G') || arg.startsWith('C'))) {
          try {
            const addr = Address.fromString(arg);
            return addr.toScVal();
          } catch (e) {
            // If not a valid address, treat as regular string
            // console.warn(`[ContractClient] Failed to convert string to Address: ${arg}`, e);
            return xdr.ScVal.scvString(arg);
          }
        }
        return xdr.ScVal.scvString(arg);
      }
      // Convert Uint8Array/Buffer to ScBytes ScVal using nativeToScVal
      if (arg instanceof Uint8Array) {
        return nativeToScVal(arg);
      }
      if (typeof Buffer !== 'undefined' && Buffer.isBuffer(arg)) {
        // Convert Buffer to Uint8Array for nativeToScVal
        return nativeToScVal(new Uint8Array(arg));
      }
      if (arg === null || arg === undefined) {
        // For Option<T> in Rust, null/undefined means None, which is scvVoid
        return xdr.ScVal.scvVoid();
      }
      // For objects (like structs), pass through - Contract.call() will use spec to convert
      // The contract spec knows the struct format and will convert properly with symbol keys
      return arg;
    });
    
    // Resolve V2 function name and route to the correct V2 contract
    const v2FunctionName = this.getV2FunctionName(functionName);
    const v2Contract = this.getContractForFn(v2FunctionName);

    // Check if this is a read-only operation (V1 and V2 names both covered)
    const isReadOperation = [
      'get_admin', 'get_country_policy', 'get_country_allowed', 'list_allowed_countries',
      'get_session', 'get_session_groups', 'get_live_session_id', 'get_verifier', 'get_webauthn_verifier',
      'get_trust_group', 'get_anchored_event', 'get_last_cell_id',
      // V2 vault reads
      'balance', 'total_vault_balance', 'get_passkey',
      // V2 compliance reads
      'get_user_compliance_state', 'get_kyc_data',
      // V2 core reads
      'get_country_info', 'get_country_admin', 'get_registry',
      // V1 names that map to V2 reads (resolve the rename)
      'get_balance', 'get_total_vault_balance', 'get_passkey_pubkey',
    ].includes(v2FunctionName);
    
    if (isReadOperation) {
      // For read operations, use simulation only (no signing/sending)
      // console.log(`[ContractClient.call] Read operation ${functionName}, using simulation only...`);
      
      // Build a minimal transaction for simulation
      const contractOp = v2Contract.call(v2FunctionName, ...scValArgs);
      const transaction = new TransactionBuilder(sourceAccount, {
        fee: BASE_FEE,
        networkPassphrase: this.network,
      })
        .addOperation(contractOp)
        .setTimeout(300)
        .build();

      // Simulate to get the result
      let simResponse: rpc.Api.SimulateTransactionResponse;
      try {
        simResponse = await this.rpc.simulateTransaction(transaction);
      } catch (simError: any) {
        // Check if it's an XDR parsing error during simulation
        // This can happen when the SDK tries to parse the response and encounters an Option type
        if (simError.message?.includes('Bad union switch') || 
            simError.message?.includes('union switch') ||
            simError.message?.includes('XDR') ||
            simError.toString().includes('Bad union switch')) {
          // console.warn(`[ContractClient.call] XDR parsing error during simulation for ${functionName} - contract format may have changed or SDK version mismatch:`, simError.message || simError.toString());
          // For read operations, return a special marker for XDR errors so callers can distinguish
          // from actual null returns (e.g., Option::None)
          if (isReadOperation) {
            // Return a special marker that indicates XDR error (not actual null)
            return 'XDR_PARSING_ERROR' as any;
          }
          // For write operations, we can't proceed without simulation
          throw new Error(`XDR_PARSING_ERROR:${simError.message || simError.toString()}`);
        }
        throw simError;
      }
      
      // Check if simulation failed
      if ('error' in simResponse) {
        // console.error(`[ContractClient.call] Simulation failed for ${functionName}:`, simResponse.error);
        throw new Error(`Simulation failed: ${JSON.stringify(simResponse.error)}`);
      }
      
      // Extract return value from simulation
      if (simResponse && 'result' in simResponse && simResponse.result) {
        const returnValue = simResponse.result.retval;
        if (returnValue) {
          try {
            const converted = this.scValToJs(returnValue);
            // console.log(`[ContractClient.call] Read operation ${functionName} result:`, converted, typeof converted);
            return converted;
          } catch (parseError: any) {
            // If parsing fails with "Bad union switch", the contract might have changed
            if (parseError.message?.includes('Bad union switch') || 
                parseError.message?.includes('union switch') ||
                parseError.message?.includes('XDR') ||
                parseError.toString().includes('Bad union switch')) {
              // console.warn(`[ContractClient.call] XDR parsing error for ${functionName} - contract format may have changed:`, parseError.message || parseError.toString());
              // For read operations, return a special marker for XDR errors
              return 'XDR_PARSING_ERROR' as any;
            }
            throw parseError;
          }
        }
      }
      
      return null;
    }
    
    // For write operations, build, sign, and send transaction via the correct V2 contract.
    if (false as unknown as boolean) {
      // removed — was V1 spec-refresh dead code
      let spec: any;
      spec = this.coreContract;
        
        // If spec is not available, force load it by calling a read-only function
        // This will trigger the SDK to fetch the spec from the network
        // Per Stella's guidance: After contract upgrade, we need to refresh the contract spec
        if (!spec) {
          // console.log('[ContractClient.call] Contract spec not loaded - forcing refresh after upgrade (per Stella)...');
          try {
            // Per Stella's guidance: "Clear any cached specs in your application that might be holding onto the old contract interface"
            // Method 1: Create a new Contract instance to clear cache
            // console.log('[ContractClient.call] Creating new Contract instance to clear cached spec...');
            // @ts-ignore
            this.contract = new Contract(this.contractId);
            
            // Method 2: Try contract.getSpec() per Stella's latest guidance
            // Stella shows: await contract.getSpec() to fetch the current spec from the network
            try {
              // @ts-ignore - getSpec might not exist in all SDK versions
              if (typeof this.contract.getSpec === 'function') {
                // console.log('[ContractClient.call] Trying contract.getSpec() per Stella\'s guidance...');
                // @ts-ignore
                await this.contract.getSpec();
                // @ts-ignore
                spec = this.contract.spec;
                if (spec) {
                  // console.log('[ContractClient.call] Γ£à Contract spec loaded via getSpec()');
                } else {
                  // console.warn('[ContractClient.call] ΓÜá∩╕Å getSpec() completed but spec still not available');
                }
              } else {
                throw new Error('contract.getSpec() not available');
              }
            } catch (e1) {
              // console.warn('[ContractClient.call] contract.getSpec() not available, trying alternative method:', e1);
              // Fallback: Call a read-only function to trigger spec loading from network
              try {
                const tempContractOp = this.coreContract.call('get_admin');
                const tempSourceAccount = await this.rpc.getAccount(publicKey);
                const tempTransaction = new TransactionBuilder(tempSourceAccount, {
                  fee: BASE_FEE,
                  networkPassphrase: this.network,
                })
                  .addOperation(tempContractOp)
                  .setTimeout(30)
                  .build();
                // Simulate to trigger spec loading
                try {
                  await this.rpc.simulateTransaction(tempTransaction);
                  // @ts-ignore
                  spec = this.contract.spec;
                  if (spec) {
                    // console.log('[ContractClient.call] Γ£à Contract spec loaded successfully after refresh');
                  } else {
                    // console.warn('[ContractClient.call] ΓÜá∩╕Å Contract spec still not available after refresh');
                    // console.warn('[ContractClient.call] ΓÜá∩╕Å RPC server may use stale spec during simulation!');
                  }
                } catch (e2) {
                  // Ignore simulation errors - we just want to trigger spec loading
                  // console.log('[ContractClient.call] Spec loading simulation completed (errors ignored)');
                  // @ts-ignore
                  spec = this.contract.spec;
                  if (!spec) {
                    // console.warn('[ContractClient.call] ΓÜá∩╕Å Contract spec still not available - RPC server may use stale spec!');
                  }
                }
              } catch (e3) {
                // console.warn('[ContractClient.call] Could not load spec via get_admin call:', e3);
              }
            }
          } catch (e) {
            // console.warn('[ContractClient.call] Could not force-load contract spec:', e);
          }
        }
        
        if (spec) {
          // @ts-ignore
          const depositFunc = spec.getFunction('deposit');
          if (depositFunc) {
            // console.log('[ContractClient.call] Deposit function spec from contract:', {
            //   name: depositFunc.name,
            //   // @ts-ignore
            //   inputs: depositFunc.inputs?.map((input: any, idx: number) => ({
            //     index: idx,
            //     name: input.name,
            //     type: input.type?.toString() || 'unknown'
            //   })) || []
            // });
            
            // Check if arg[1] (country_code) type matches BytesN<2>
            // @ts-ignore
            const countryCodeInput = depositFunc.inputs?.[1];
            if (countryCodeInput) {
              // console.log('[ContractClient.call] Contract spec says country_code type:', countryCodeInput.type?.toString());
              // @ts-ignore
              if (countryCodeInput.type?.toString().includes('BytesN') || countryCodeInput.type?.toString().includes('bytesn')) {
                // console.log('[ContractClient.call] Γ£à Contract spec correctly shows BytesN<2> for country_code');
              } else {
                // console.warn('[ContractClient.call] ΓÜá∩╕Å Contract spec shows different type for country_code:', countryCodeInput.type?.toString());
              }
            }
          }
        }
    } // end if(false) — V1 spec-refresh dead block

    // V2: all functions go through the standard Contract.call() path.
    const contractOp = v2Contract.call(v2FunctionName, ...scValArgs);
    
    // Build transaction with longer timeout (300 seconds = 5 minutes)
    // This gives plenty of time for user to approve in Freighter
    const transaction = new TransactionBuilder(sourceAccount, {
      fee: BASE_FEE,
      networkPassphrase: this.network,
    })
      .addOperation(contractOp)
      .setTimeout(300) // 5 minutes timeout
      .build();

    // Simulate to get resource usage
    
    let simResponse: rpc.Api.SimulateTransactionResponse;
    try {
      simResponse = await this.rpc.simulateTransaction(transaction);
    } catch (simError: any) {
      // Check if it's an XDR parsing error during simulation
      // This can happen when the SDK tries to parse the response and encounters an Option type
      if (simError.message?.includes('Bad union switch') || 
          simError.message?.includes('union switch') ||
          simError.message?.includes('XDR') ||
          simError.toString().includes('Bad union switch')) {
        // console.warn(`[ContractClient.call] XDR parsing error during simulation for ${functionName} - contract format may have changed or SDK version mismatch:`, simError.message || simError.toString());
        // For read operations, return null gracefully
        if (isReadOperation) {
          return null;
        }
        // For write operations, we can't proceed without simulation
        throw new Error(`XDR_PARSING_ERROR:${simError.message || simError.toString()}`);
      }
      throw simError;
    }
    
    // Check if simulation failed
    if ('error' in simResponse) {
      // console.error(`[ContractClient.call] Simulation failed for ${functionName}:`, simResponse.error);
      if (false as unknown as boolean) {
        // V1 deposit bypass removed — V2 uses standard simulation path
        // console.warn('[ContractClient.call] ΓÜá∩╕Å Simulation failed for deposit - trying to submit without simulation as diagnostic step');
        // console.warn('[ContractClient.call] ΓÜá∩╕Å This bypasses RPC server spec-based re-conversion but risks insufficient resources');
        
        // Try to submit transaction without simulation or prepareTransaction
        // This is risky but might work since our XDR is correct
        try {
          // Skip prepareTransaction (it might call simulateTransaction internally)
          // Convert transaction directly to XDR for signing
          const xdrString = transaction.toXDR();
          
          // Sign transaction
          let signed: string;
          try {
            signed = await this.wallet.signTransaction(xdrString, this.network);
          } catch (error: any) {
            if (error.message?.includes('rejected') || error.message?.includes('denied')) {
              throw new Error('Transaction was rejected by user');
            }
            throw new Error(`Failed to sign transaction: ${error.message || 'Unknown error'}`);
          }
          
          // Rebuild transaction from signed XDR
          const tx = TransactionBuilder.fromXDR(signed, this.network);
          
          // Submit transaction
          // console.log(`[ContractClient.call] Submitting deposit transaction without simulation (diagnostic)...`);
          const sendResponse = await this.rpc.sendTransaction(tx);
          
          if (sendResponse.status === 'ERROR') {
            const errorMsg = sendResponse.errorResult 
              ? JSON.stringify(sendResponse.errorResult) 
              : 'Transaction failed';
            throw new Error(errorMsg);
          }
          
          // console.log(`[ContractClient.call] Γ£à Deposit transaction submitted without simulation (diagnostic):`, sendResponse);
          
          // Wait for transaction to be included
          // console.log(`[ContractClient.call] Waiting for deposit transaction to be included...`);
          let attempts = 0;
          const maxAttempts = 30;
          while (attempts < maxAttempts) {
            try {
              const txStatus = await this.rpc.getTransaction(sendResponse.hash);
              if (txStatus.status === 'SUCCESS') {
                // console.log(`[ContractClient.call] Γ£à Deposit transaction succeeded!`);
                return this.scValToJs(txStatus.returnValue!);
              } else if (txStatus.status === 'FAILED') {
                throw new Error(`Transaction failed: ${JSON.stringify(txStatus)}`);
              }
            } catch (e: any) {
              if (e.message?.includes('not found')) {
                // Transaction not yet included, wait and retry
                await new Promise(resolve => setTimeout(resolve, 2000));
                attempts++;
                continue;
              }
              throw e;
            }
          }
          
          throw new Error('Transaction not included after 60 seconds');
        } catch (skipError: any) {
          // console.error('[ContractClient.call] Submitting without simulation also failed:', skipError);
          throw new Error(`Simulation failed: ${JSON.stringify(simResponse.error)}`);
        }
      }
      // V2: fail on simulation error for all functions
      throw new Error(`Simulation failed: ${JSON.stringify(simResponse.error)}`);
    }
    // console.log(`[ContractClient.call] Simulation successful for ${functionName}`);

    // Prepare transaction with resource estimates
    const prepared = await this.rpc.prepareTransaction(transaction);
    
    // Convert to XDR string for signing
    const xdrString = prepared.toXDR();
    
    // Sign transaction - pass network passphrase (this.network is already the passphrase string)
    let signed: string;
    try {
      signed = await this.wallet.signTransaction(xdrString, this.network);
    } catch (error: any) {
      // Check if user rejected
      if (error.message?.includes('rejected') || error.message?.includes('denied')) {
        throw new Error('Transaction was rejected by user');
      }
      // console.error('Transaction signing error:', error);
      // console.error('Transaction XDR length:', xdrString.length);
      throw new Error(`Failed to sign transaction: ${error.message || 'Unknown error'}`);
    }
    
    // Determine the confirmed transaction hash — either via the backend
    // fee-bump sponsor (service account pays the resource fee) or a direct
    // submit from the user's account.
    let txHash: string | null = null;

    if (useSponsor) {
      try {
        txHash = await this.submitViaSponsor(signed);
      } catch (sponsorErr: any) {
        // If sponsorship is unavailable, fall back to a normal user-paid
        // submit so the action still completes. Worth a warning: the action
        // succeeds either way, so the only visible sign that the service
        // account stopped paying would otherwise be the user's balance.
        console.warn(
          '[ContractClient.call] Fee sponsorship failed — submitting from the '
          + "user's own account instead:",
          sponsorErr?.message || sponsorErr,
        );
        txHash = null;
      }
    }

    if (!txHash) {
      // Rebuild transaction from signed XDR to ensure it's fresh
      const tx = TransactionBuilder.fromXDR(signed, this.network);

      // Send transaction immediately after signing
      const sendResponse = await this.rpc.sendTransaction(tx);

      if (sendResponse.status === 'ERROR') {
        const errorMsg = sendResponse.errorResult
          ? JSON.stringify(sendResponse.errorResult)
          : 'Transaction failed';
        throw new Error(errorMsg);
      }
      txHash = sendResponse.hash;
    }

    // Poll for the specific transaction to be confirmed (SUCCESS or FAILED).
    // This is reliable — we don't proceed until this exact TX is in the ledger.
    {
      const confirmHash: string = txHash;
      let confirmed = false;
      const maxAttempts = 30; // up to 30 seconds
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        try {
          const txResult = await this.rpc.getTransaction(confirmHash);
          if (txResult.status === 'SUCCESS') {
            confirmed = true;
            // Return the actual on-chain result when available
            if (txResult.returnValue) {
              try { return this.scValToJs(txResult.returnValue); } catch { /* fall through */ }
            }
            break;
          } else if (txResult.status === 'FAILED') {
            throw new Error(`Transaction failed on-chain: ${JSON.stringify(txResult)}`);
          }
          // status === 'NOT_FOUND' means still pending — keep polling
        } catch (e: any) {
          if (e.message?.includes('Transaction failed on-chain')) throw e;
          // NOT_FOUND or network hiccup — keep polling
        }
      }
      if (!confirmed) {
        // Timed out waiting — the TX was submitted but we couldn't confirm inclusion.
        // Best-effort: wait one more ledger close before continuing.
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
    }
    
    // Use simulation result for return value (simulation is reliable for return values)
    if (simResponse && 'result' in simResponse && simResponse.result) {
      const returnValue = simResponse.result.retval;
      if (returnValue) {
        return this.scValToJs(returnValue);
      }
    }
    
    return null;
  }

  /**
   * Submit an already-signed inner Soroban transaction through the backend
   * fee-bump sponsor. The service account wraps it in a FeeBumpTransaction and
   * pays the Soroban resource fee, so the user's account is not charged.
   * Returns the confirmed fee-bump transaction hash, or throws on failure so
   * the caller can fall back to a direct user-paid submit.
   */
  private async submitViaSponsor(signedXdr: string): Promise<string> {
    const resp = await fetch(`${CONTRACT_BACKEND_BASE}/api/v2/session/sponsor`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ signedXdr }),
    });
    const data = await resp.json().catch(() => null);
    if (!resp.ok) {
      // The body carries the decoded reason — an unfunded service account, a
      // rejected submit and a contract error all arrive as a 500, so dropping
      // it here left the status code as the only clue.
      const reason = data?.error || `${resp.status} ${resp.statusText}`;
      throw new Error(
        `sponsor_http_${resp.status}: ${reason}`
        + (data?.code != null ? ` (code ${data.code})` : ''),
      );
    }
    if (!data || !data.success || !data.hash) {
      throw new Error(`sponsor_failed:${data?.error || 'unknown'}`);
    }
    return data.hash as string;
  }

  private scValToJs(scVal: xdr.ScVal): any {
    switch (scVal.type) {
      case 'scvBool':
        return scVal.b;
      case 'scvU32':
        return scVal.u32;
      case 'scvI32':
        return scVal.i32;
      case 'scvU64':
        return scVal.u64;
      case 'scvI64':
        return scVal.i64;
      case 'scvAddress':
        // Address covers every ScAddress arm Protocol 28 defines: account, muxed
        // account, contract, claimable balance, and liquidity pool.
        try {
          return Address.fromScAddress(scVal.address).toString();
        } catch (e) {
          // console.error('[ContractClient] Error converting scvAddress:', e);
          return null;
        }
      case 'scvVoid':
        // Void means None for Option types
        return null;
      case 'scvBytes':
        // ScBytes wraps the payload rather than being a Uint8Array itself.
        return scVal.bytes.toBytes();
      case 'scvString':
        return scVal.str.toString();
      case 'scvVec': {
        const vec = scVal.vec?.map((v) => this.scValToJs(v)) || [];
        // Option<Address> might be returned as Vec with one element
        // If it's a single-element vec with an address, return the address string directly
        // Stellar addresses can start with 'G' (account) or 'C' (contract)
        if (vec.length === 1 && typeof vec[0] === 'string' && vec[0].length === 56 && (vec[0].startsWith('G') || vec[0].startsWith('C'))) {
          return vec[0];
        }
        return vec;
      }
      case 'scvMap': {
        const map: any = {};
        scVal.map?.forEach((entry) => {
          // Symbol keys are used for struct fields
          const key = entry.key.type === 'scvSymbol'
            ? entry.key.sym.toString()
            : String(this.scValToJs(entry.key));
          map[key] = this.scValToJs(entry.val);
        });
        return map;
      }
      default:
        return scVal;
    }
  }

  // ========== Trust Group Functions ==========

  async createTrustGroup(name: string): Promise<number> {
    // console.log('[ContractClient] Creating trust group:', name);
    const callerStr = await this.wallet.getPublicKey();
    // V2: create_trust_group(owner, name, members: Vec<Address>) — pass empty vec
    const emptyMembersScVal = xdr.ScVal.scvVec([]);
    const result = await this.call('create_trust_group', callerStr, name, emptyMembersScVal);
    return result as number;
  }

  async addToTrustGroup(groupId: number, memberAddress: string): Promise<void> {
    // console.log('[ContractClient] Adding member to trust group:', groupId, memberAddress);
    const callerStr = await this.wallet.getPublicKey();
    // V2: renamed to add_trust_group_member (routing map handles the rename)
    await this.call('add_to_trust_group', callerStr, groupId, memberAddress);
  }

  async removeFromTrustGroup(groupId: number, memberAddress: string): Promise<void> {
    // console.log('[ContractClient] Removing member from trust group:', groupId, memberAddress);
    const callerStr = await this.wallet.getPublicKey();
    // V2: renamed to remove_trust_group_member (routing map handles the rename)
    await this.call('remove_from_trust_group', callerStr, groupId, memberAddress);
  }

  async getTrustGroup(groupId: number): Promise<any> {
    const result = await this.call('get_trust_group', groupId);
    return result;
  }

  /**
   * GeoSession V2 does not expose `list_my_trust_groups` on-chain.
   * Scan `get_trust_group` ids (same strategy as ReadOnlyContractClient.listTrustGroupIdsForWallet).
   */
  async listMyTrustGroups(maxScan = 256): Promise<number[]> {
    const callerStr = await this.wallet.getPublicKey();
    const inGroup = (group: any): boolean => {
      if (!group) return false;
      const owner = group.owner ?? group.admin;
      if (owner && String(owner) === callerStr) return true;
      const mem = group.members;
      if (!Array.isArray(mem)) return false;
      return mem.some((m: any) => String(m) === callerStr);
    };
    const ids: number[] = [];
    const batch = 16;
    for (let start = 1; start <= maxScan; start += batch) {
      const end = Math.min(start + batch - 1, maxScan);
      const slice: number[] = [];
      for (let id = start; id <= end; id++) slice.push(id);
      const rows = await Promise.all(
        slice.map(async (id) => {
          try {
            const group = await this.getTrustGroup(id);
            return { id, group };
          } catch {
            return { id, group: null };
          }
        }),
      );
      for (const { id, group } of rows) {
        if (inGroup(group)) ids.push(id);
      }
    }
    return ids.sort((a, b) => a - b);
  }

  async createPrivateSessionWithTrustGroup(trustGroupId: number): Promise<number> {
    // console.log('[ContractClient] Creating private session with trust group:', trustGroupId);
    // V2: create_trust_session is folded into create_session(is_public=false, country=None, trust_group_id=Some(id))
    // Sponsor the fee so creating a trust-group room doesn't charge the user.
    this.sponsorNextSubmit = true;
    const result = await this.call('create_session', false, null, trustGroupId);
    return result as number;
  }

  async endSession(sessionId: number): Promise<void> {
    // console.log('[ContractClient] Ending session:', sessionId);
    // V2: end_session takes only session_id (no caller — no require_auth)
    // Sponsor the fee so ending a session doesn't charge the user.
    this.sponsorNextSubmit = true;
    await this.call('end_session', sessionId);
  }

  /**
   * Check if passkey is registered for a user address
   */
  async isPasskeyRegistered(userAddress: string): Promise<boolean> {
    try {
      const result = await this.call('get_passkey_pubkey', userAddress);
      return result !== null && result !== undefined;
    } catch (error) {
      // console.error('[ContractClient] Failed to check passkey registration:', error);
      return false;
    }
  }

  /**
   * Register passkey public key for a user address
   */
  async registerPasskey(userAddress: string, passkeyPubkey: string): Promise<boolean> {
    try {
      // Convert base64 public key to Bytes
      const passkeyPubkeyBytes = Buffer.from(passkeyPubkey, 'base64');
      if (passkeyPubkeyBytes.length !== 65) {
        throw new Error('Passkey public key must be 65 bytes');
      }
      
      // Call contract register_passkey function
      const result = await this.call('register_passkey', userAddress, passkeyPubkeyBytes);
      return result === true;
    } catch (error) {
      // console.error('[ContractClient] Failed to register passkey:', error);
      throw error;
    }
  }

  /**
   * Get total vault balance for a country and asset (sum of all user balances)
   * Can be called without wallet connection
   */
  async getTotalVaultBalance(countryCode: string, asset: string): Promise<number> {
    try {
      // V2: renamed to 'total_vault_balance', country is u32 numeric
      const numericCountry = this.alpha2ToNumeric(countryCode);
      const result = await this.call('get_total_vault_balance', numericCountry, asset);
      if (typeof result === 'number') {
        return result;
      }
      if (typeof result === 'bigint') {
        return Number(result);
      }
      if (result && typeof result === 'object' && 'lo' in result) {
        return Number(result.lo || 0);
      }
      return 0;
    } catch (error) {
      // console.error('[ContractClient] Failed to get total vault balance:', error);
      return 0;
    }
  }

  /**
   * Transfer funds between country vaults
   */
  async transferBetweenCountries(
    userAddress: string,
    fromCountryCode: string,
    toCountryCode: string,
    asset: string,
    amount: number
  ): Promise<boolean> {
    try {
      // Convert country codes to Bytes ScVals (matching deposit function)
      const fromCountryCodeBytes = Buffer.from(fromCountryCode.toUpperCase(), 'utf8');
      const toCountryCodeBytes = Buffer.from(toCountryCode.toUpperCase(), 'utf8');
      
      if (fromCountryCodeBytes.length !== 2 || toCountryCodeBytes.length !== 2) {
        throw new Error('Country codes must be exactly 2 bytes');
      }
      
      // Create Bytes ScVals (like deposit function does)
      const fromCountryCodeScVal = xdr.ScVal.scvBytes(fromCountryCodeBytes);
      const toCountryCodeScVal = xdr.ScVal.scvBytes(toCountryCodeBytes);
      
      const result = await this.call(
        'transfer_between_countries',
        userAddress,
        fromCountryCodeScVal, // Pass ScVal, not Buffer
        toCountryCodeScVal,    // Pass ScVal, not Buffer
        asset,
        amount
      );
      
      return result === true;
    } catch (error) {
      // console.error('[ContractClient] Failed to transfer between countries:', error);
      throw error;
    }
  }

  /**
   * Set transfer policy between two countries (admin only)
   */
  async setTransferAllowed(
    fromCountryCode: string,
    toCountryCode: string,
    allowed: boolean
  ): Promise<void> {
    const fromNumeric = ContractClient.ALPHA2_TO_NUMERIC[fromCountryCode.toUpperCase()];
    const toNumeric   = ContractClient.ALPHA2_TO_NUMERIC[toCountryCode.toUpperCase()];
    if (!fromNumeric) throw new Error(`Unknown country code: ${fromCountryCode}`);
    if (!toNumeric)   throw new Error(`Unknown country code: ${toCountryCode}`);
    await this.call('set_transfer_allowed', fromNumeric, toNumeric, allowed);
  }

  /**
   * Set compliance configuration for a country (admin only)
   */
  // ========== KYC/AML Functions (SEP-0009) ==========

  /**
   * Submit KYC data for a user in a country
   * @param countryCode ISO 3166-1 alpha-2 country code (e.g., "US")
   * @param kycData KYC data structure based on SEP-0009
   */
  async submitKYCData(
    countryCode: string,
    kycData: {
      familyName?: string;
      givenName?: string;
      additionalName?: string;
      addressCountryCode?: string; // ISO 3166-1 alpha-3
      stateOrProvince?: string;
      city?: string;
      postalCode?: string;
      address?: string; // Full address as multi-line string
      mobileNumber?: string; // E.164 format
      emailAddress?: string;
      birthDate?: string; // ISO 8601 date format
      birthPlace?: string;
      birthCountryCode?: string; // ISO 3166-1 alpha-3
      taxId?: string;
      taxIdName?: string;
      occupation?: number; // ISCO08 code
      employerName?: string;
      employerAddress?: string; // SEP-0009: employer address
      languageCode?: string; // ISO 639-1
      idType?: string; // passport, drivers_license, id_card, etc.
      idCountryCode?: string; // ISO 3166-1 alpha-3
      idNumber?: string;
      idIssueDate?: string; // ISO 8601 date
      idExpirationDate?: string; // ISO 8601 date
      sex?: string; // male, female, other
      referralId?: string;
    }
  ): Promise<void> {
    const userAddress = await this.wallet.getPublicKey();
    
    // Convert to contract format (all fields optional)
    const contractKYCData = {
      family_name: kycData.familyName || null,
      given_name: kycData.givenName || null,
      additional_name: kycData.additionalName || null,
      address_country_code: kycData.addressCountryCode || null,
      state_or_province: kycData.stateOrProvince || null,
      city: kycData.city || null,
      postal_code: kycData.postalCode || null,
      address: kycData.address || null,
      mobile_number: kycData.mobileNumber || null,
      email_address: kycData.emailAddress || null,
      birth_date: kycData.birthDate || null,
      birth_place: kycData.birthPlace || null,
      birth_country_code: kycData.birthCountryCode || null,
      tax_id: kycData.taxId || null,
      tax_id_name: kycData.taxIdName || null,
      occupation: kycData.occupation || null,
      employer_name: kycData.employerName || null,
      employer_address: kycData.employerAddress || null,
      language_code: kycData.languageCode || null,
      id_type: kycData.idType || null,
      id_country_code: kycData.idCountryCode || null,
      id_number: kycData.idNumber || null,
      id_issue_date: kycData.idIssueDate || null,
      id_expiration_date: kycData.idExpirationDate || null,
      sex: kycData.sex || null,
      referral_id: kycData.referralId || null,
      kyc_status: 'pending',
      kyc_verified_at: null,
      kyc_verified_by: null
    };
    
    await this.call('submit_kyc_data', userAddress, countryCode, contractKYCData);
  }

  /**
   * Get KYC data for a user in a country
   * @param userAddress User's Stellar address
   * @param countryCode ISO 3166-1 alpha-2 country code
   */
  async getKYCData(userAddress: string, countryCode: string): Promise<{
    familyName?: string;
    givenName?: string;
    additionalName?: string;
    addressCountryCode?: string;
    stateOrProvince?: string;
    city?: string;
    postalCode?: string;
    address?: string;
    mobileNumber?: string;
    emailAddress?: string;
    birthDate?: string;
    birthPlace?: string;
    birthCountryCode?: string;
    taxId?: string;
    taxIdName?: string;
    occupation?: number;
    employerName?: string;
    languageCode?: string;
    idType?: string;
    idCountryCode?: string;
    idNumber?: string;
    idIssueDate?: string;
    idExpirationDate?: string;
    sex?: string;
    referralId?: string;
    kycStatus: string;
    kycVerifiedAt?: number;
    kycVerifiedBy?: string;
  } | null> {
    try {
      const result = await this.call('get_kyc_data', userAddress, countryCode);
      if (!result || result === null || result === undefined) {
        return null;
      }
      
      // Convert from contract format to frontend format
      return {
        familyName: result.family_name || undefined,
        givenName: result.given_name || undefined,
        additionalName: result.additional_name || undefined,
        addressCountryCode: result.address_country_code || undefined,
        stateOrProvince: result.state_or_province || undefined,
        city: result.city || undefined,
        postalCode: result.postal_code || undefined,
        address: result.address || undefined,
        mobileNumber: result.mobile_number || undefined,
        emailAddress: result.email_address || undefined,
        birthDate: result.birth_date || undefined,
        birthPlace: result.birth_place || undefined,
        birthCountryCode: result.birth_country_code || undefined,
        taxId: result.tax_id || undefined,
        taxIdName: result.tax_id_name || undefined,
        occupation: result.occupation || undefined,
        employerName: result.employer_name || undefined,
        languageCode: result.language_code || undefined,
        idType: result.id_type || undefined,
        idCountryCode: result.id_country_code || undefined,
        idNumber: result.id_number || undefined,
        idIssueDate: result.id_issue_date || undefined,
        idExpirationDate: result.id_expiration_date || undefined,
        sex: result.sex || undefined,
        referralId: result.referral_id || undefined,
        kycStatus: result.kyc_status || 'pending',
        kycVerifiedAt: result.kyc_verified_at || undefined,
        kycVerifiedBy: result.kyc_verified_by || undefined
      };
    } catch (error) {
      // console.error('[ContractClient] Failed to get KYC data:', error);
      return null;
    }
  }

  /**
   * Verify/Approve KYC data (admin only)
   * @param userAddress User's Stellar address
   * @param countryCode ISO 3166-1 alpha-2 country code
   * @param status "approved" or "rejected"
   */
  async verifyKYCData(userAddress: string, countryCode: string, status: 'approved' | 'rejected'): Promise<void> {
    await this.call('verify_kyc_data', userAddress, countryCode, status);
  }

  /**
   * Check if user has approved KYC for a country
   * @param userAddress User's Stellar address
   * @param countryCode ISO 3166-1 alpha-2 country code
   */
  async hasApprovedKYC(userAddress: string, countryCode: string): Promise<boolean> {
    try {
      const result = await this.call('has_approved_kyc', userAddress, countryCode);
      return result === true;
    } catch (error) {
      // console.error('[ContractClient] Failed to check KYC status:', error);
      return false;
    }
  }

  async setComplianceConfig(
    countryCode: string,
    config: {
      kycRequired: boolean;
      dailyLimit: number;
      monthlyLimit: number;
      sanctionsScreened: boolean;
      restricted: boolean;
      minTransactionAmount: number;
      maxTransactionAmount: number;
    }
  ): Promise<void> {
    try {
      // Convert country code to Bytes ScVal
      const countryCodeBytes = Buffer.from(countryCode.toUpperCase(), 'utf8');
      if (countryCodeBytes.length !== 2) {
        throw new Error(`Invalid country code length: expected 2 bytes, got ${countryCodeBytes.length}`);
      }
      const countryCodeScVal = xdr.ScVal.scvBytes(countryCodeBytes);
      
      const complianceConfig = {
        kyc_required: config.kycRequired,
        daily_limit: config.dailyLimit,
        monthly_limit: config.monthlyLimit,
        sanctions_screened: config.sanctionsScreened,
        restricted: config.restricted,
        min_transaction_amount: config.minTransactionAmount,
        max_transaction_amount: config.maxTransactionAmount
      };
      
      await this.call(
        'set_compliance_config',
        countryCodeScVal,
        complianceConfig
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Set the full CountryPolicyV2 for a country on the compliance contract.
   * countryNumeric is the ISO 3166-1 numeric code (e.g. 840 for US).
   */
  async setCountryPolicy(countryNumeric: number, policy: {
    depositsEnabled: boolean;
    withdrawalsEnabled: boolean;
    transfersInEnabled: boolean;
    transfersOutEnabled: boolean;
    kycRequired: boolean;
    sanctionsScreened: boolean;
    restricted: boolean;
    minTxAmount: number;
    maxTxAmount: number;
    dailyLimit: number;
    monthlyLimit: number;
    feeBps: number;
    countryAdminShareBps: number;
    requireWebauthnWithdraw: boolean;
    requireZkForSessionJoin: boolean;
    allowPublicSessions: boolean;
    policyVersion: number;
  }): Promise<void> {
    // Build the XDR ScMap manually so BigInt i128 fields are properly encoded
    // and never hit JSON.stringify (which doesn't support BigInt).
    // ScMap keys must be sorted lexicographically by symbol name.
    const bool = (b: boolean) => xdr.ScVal.scvBool(b);
    const u32  = (n: number)  => xdr.ScVal.scvU32(n);
    const i128 = (n: number)  => {
      const v = BigInt(n);
      const lo = v & BigInt('0xFFFFFFFFFFFFFFFF');
      const hi = v >> BigInt(64);
      return xdr.ScVal.scvI128(new xdr.Int128Parts({
        hi: xdr.Int64.fromString(hi.toString()),
        lo: xdr.Uint64.fromString(lo.toString()),
      }));
    };
    const sym  = (s: string)  => xdr.ScVal.scvSymbol(s);
    const entry = (k: string, v: xdr.ScVal) =>
      new xdr.ScMapEntry({ key: sym(k), val: v });

    // Entries sorted lexicographically by key (required by Soroban)
    const policyScVal = xdr.ScVal.scvMap([
      entry('allow_public_sessions',     bool(policy.allowPublicSessions)),
      entry('country_admin_share_bps',   i128(policy.countryAdminShareBps)),
      entry('daily_limit',               i128(policy.dailyLimit)),
      entry('deposits_enabled',          bool(policy.depositsEnabled)),
      entry('fee_bps',                   i128(policy.feeBps)),
      entry('kyc_required',              bool(policy.kycRequired)),
      entry('max_tx_amount',             i128(policy.maxTxAmount)),
      entry('min_tx_amount',             i128(policy.minTxAmount)),
      entry('monthly_limit',             i128(policy.monthlyLimit)),
      entry('policy_version',            u32(policy.policyVersion)),
      entry('require_webauthn_withdraw', bool(policy.requireWebauthnWithdraw)),
      entry('require_zk_for_session_join', bool(policy.requireZkForSessionJoin)),
      entry('restricted',                bool(policy.restricted)),
      entry('sanctions_screened',        bool(policy.sanctionsScreened)),
      entry('transfers_in_enabled',      bool(policy.transfersInEnabled)),
      entry('transfers_out_enabled',     bool(policy.transfersOutEnabled)),
      entry('withdrawals_enabled',       bool(policy.withdrawalsEnabled)),
    ]);
    await this.call('set_country_policy', countryNumeric, policyScVal);
  }
}